import { useRef } from 'react';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { Separator } from 'office-ui-fabric-react/lib/Separator';
import DocumentTitle from 'react-document-title';
import './Payments.sass';
import * as Style from './RePaymentSubscriptionStyle';
import React from 'react';
import CustomIcon from '@/components/icons/CustomIcon';
import ElementService from '@/services/ElementsService';
import { decimalFormatter, longDecimalFormatter } from '@/utils/numberFormatter';
import Auth from '@/Auth/Auth';
import SubscriptionService, { SubscriptionType } from '@/services/SubscriptionService';
import PaymentService from '@/services/PaymentService';
import InespayService, { InespayNoPrice, InespayType } from '@/services/InespayService';
import { parseInespayResult, isInespaySuccess } from '@/utils/InespayResponse';
import UsersSubscription, { TRenewalOptionsDTO, TSubscriptionOptionsDTO } from '@/services/UsersSubscriptionService';
import { isPromotionSubscriptionType, resolveRenewalSurcharge, resolveRenewalTarget } from './subscriptionOptionsRules';
import { resolveElementEarthPrice } from './elementsEarthRules';
import { buildSubscriptionSubject } from './paymentSubjectRules';
import { elementsToFill, exceedsElementsNeeded, TRenewalElementInputs, TRenewalElementPrices } from './renewalElementsRules';

interface RouteParams {};
interface Props extends RouteComponentProps<RouteParams> {};

export interface ContractsUnderSameSubscription
{
    subscriptionInfo: Contracts[];
    dateOfOutdate: string;
    outdateRecharge: number;
    subscriptionId: number;
    subscriptionType: string;
    subscriptionLimit: number;
    subscriptionName: string;
    subscriptionPeriod: string;
    subscriptionPrice: number;
    userSubscriptionId: number;
}
export interface Contracts
{
    elementsWater?: number;
    elementsIA?: number;
    elementsEarth?: number;
    savingPlanId?: number;
    savingPlanName?: string;
    elementAmountId?: number;
    contract?: string;
    isSubscriptionRenewalBeforeExpiration?: boolean;
    hasElements?: boolean;
}

const RePaymentSubscription: React.FunctionComponent<Props> = () =>
{
    const { state } = useLocation<any>();
    var activePlan = Auth.getActivePlan();

    const validateContractsData = (): boolean =>
    {
        if(!state || !state.contracts)
        {
            console.error('RePaymentSubscription: No se recibieron datos de contratos en el state');
            return false;
        }

        const contractsData = state.contracts;

        if(!contractsData.subscriptionInfo || !Array.isArray(contractsData.subscriptionInfo))
        {
            console.error('RePaymentSubscription: subscriptionInfo es inválido o no es un array');
            return false;
        }

        if(contractsData.subscriptionInfo.length === 0)
        {
            console.error('RePaymentSubscription: subscriptionInfo está vacío');
            return false;
        }

        if(typeof contractsData.subscriptionPrice !== 'number')
        {
            console.error('RePaymentSubscription: subscriptionPrice no es un número válido');
            return false;
        }

        if(!contractsData.dateOfOutdate || typeof contractsData.dateOfOutdate !== 'string')
        {
            console.error('RePaymentSubscription: dateOfOutdate no es válido');
            return false;
        }

        if(typeof contractsData.outdateRecharge !== 'number')
        {
            console.error('RePaymentSubscription: outdateRecharge no es un número válido');
            return false;
        }

        if(typeof contractsData.subscriptionId !== 'number')
        {
            console.error('RePaymentSubscription: subscriptionId no es un número válido');
            return false;
        }

        if(typeof contractsData.userSubscriptionId !== 'number')
        {
            console.error('RePaymentSubscription: userSubscriptionId no es un número válido');
            return false;
        }

        return true;
    };

    const resolveChosenSubscriptionId = (): number | undefined =>
    {
        const fromState = state?.newSubscriptionId;
        if(fromState != null && !Number.isNaN(Number(fromState)))
        {
            return Number(fromState);
        }

        if(state?.fromMorePlans === true)
        {
            const stored = Auth.getRepaymentSubscription();
            const parsed = parseInt(stored as string, 10);
            if(!Number.isNaN(parsed))
            {
                return parsed;
            }
        }

        Auth.deleteRepaymentSubscription();
        return undefined;
    };

    const [hasValidData] = React.useState<boolean>(validateContractsData());
    
    // Solo inicializar contracts si los datos son válidos
    const contracts = useRef<ContractsUnderSameSubscription>(state?.contracts);
    const arePricesLoaded = useRef<boolean>(false);
    const subscriptionHasNoElements = hasValidData ? contracts.current.subscriptionInfo.every(p => p.hasElements === false) : false;
    const [loadingPage, setLoadingPage] = React.useState<boolean>(true);
    const [elementWaterPrice, setElementWaterPrice] = React.useState<number>(0);
    const [elementsWaterUsed, setElementsWaterUsed] = React.useState<number>(0);
    const [elementsIAUsed, setElementsIAUsed] = React.useState<number>(0);
    const [infoMessage, setInfoMessage] = React.useState<string | undefined>(undefined);
    const [initialPrice, setInitialPrice] = React.useState<number>(hasValidData ? contracts.current.subscriptionPrice : 0);
    const [finalPrice, setFinalPrice] = React.useState<number>(hasValidData ? contracts.current.subscriptionPrice : 0);
    const [newDateSubscription, setNewDateSubscription] = React.useState<string>(hasValidData ? contracts.current.dateOfOutdate : '');
    const [inputWaterValue, setInputWaterValue] = React.useState<number[]>([]);
    const [inputIaValue, setInputIaValue] = React.useState<number[]>([]);
    // Elements Tierra: el precio lo da el backend en subscriptionOptions; con 0 (sin precio) quedan deshabilitados.
    const [elementEarthPrice, setElementEarthPrice] = React.useState<number>(0);
    const [elementsEarthUsed, setElementsEarthUsed] = React.useState<number>(0);
    const [inputEarthValue, setInputEarthValue] = React.useState<number[]>([]);
    const earthValueUsed = elementsEarthUsed * elementEarthPrice;
    const [newSubscription, setSubscription] = React.useState<SubscriptionType>();
    const [recharge, setRecharge] = React.useState<number>(hasValidData ? contracts.current.outdateRecharge : 0);
    const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
    // Opciones de renovación del backend (GET subscriptionOptions): tarifa requerida y si se puede renovar.
    const renewalOptions = useRef<TRenewalOptionsDTO | undefined>(undefined);
    // Type de la tarifa actual según el backend (una promoción se renueva sin recargo y con validez desde hoy).
    const currentSubscriptionType = useRef<string | undefined>(undefined);
    const [renewalOptionsLoaded, setRenewalOptionsLoaded] = React.useState<boolean>(false);
    const [renewalNotice, setRenewalNotice] = React.useState<string | undefined>(undefined);
    const [renewalBlockedReason, setRenewalBlockedReason] = React.useState<string | undefined>(undefined);
    
    // Validar que el usuario está autenticado - usar useState para evitar recálculo en cada render
    const [user] = React.useState(() =>
    {
        const userData = Auth.getUserProfile();
        
        if(!userData || !userData.id)
        {
            console.error('RePaymentSubscription: No se pudo obtener el perfil del usuario');
            return null;
        }
        
        return userData;
    });
    
    const today = new Date();
    today.setDate(today.getDate() + 1);
    const [reference, setReference] = React.useState<string>(hasValidData && user ? `${user.id}-S${contracts.current.subscriptionId}-A${activePlan.id}-${today.valueOf().toString()}` : '');

    React.useEffect(() =>
    {
        if(!hasValidData)
        {
            setRenewalOptionsLoaded(true);
            return;
        }

        UsersSubscription.getSubscriptionOptions(contracts.current.userSubscriptionId).then((response: TSubscriptionOptionsDTO) =>
        {
            renewalOptions.current = response?.renewal;
            currentSubscriptionType.current = response?.currentSubscription?.type;
            setElementEarthPrice(resolveElementEarthPrice(response?.elementEarthPrice));
            if(response?.renewal && response.renewal.allowed !== true)
            {
                setRenewalBlockedReason(response.renewal.notAllowedReason || 'No es posible renovar tu suscripción. Por favor, contacte con soporte.');
            }
        })
        .catch((error: any) =>
        {
            // Si falla la consulta se mantiene el comportamiento anterior: el backend valida la tarifa al cobrar.
            console.error('RePaymentSubscription: error en getSubscriptionOptions', error);
        })
        .finally(() => setRenewalOptionsLoaded(true));
    }, [hasValidData]);

    React.useEffect(() =>
    {
        // No ejecutar si los datos no son válidos
        if(!hasValidData)
        {
            setLoadingPage(false);
            return;
        }

        // No ejecutar si el usuario no está autenticado
        if(!user)
        {
            console.error('RePaymentSubscription: Usuario no autenticado');
            setLoadingPage(false);
            setInfoMessage('Error de autenticación. Por favor, inicia sesión nuevamente.');
            return;
        }

        // Se espera a las opciones de renovación para saber si hay que cambiar a la tarifa requerida.
        if(!renewalOptionsLoaded)
        {
            return;
        }

        if(arePricesLoaded.current === false)
        {
            const arrayElements = Array(contracts.current.subscriptionInfo.length).fill(0);
            setInputWaterValue(arrayElements);
            setInputIaValue(arrayElements);
            setInputEarthValue(arrayElements);

            var total = arrayElements.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
            setElementsIAUsed(total);
            setElementsWaterUsed(total);
            setElementsEarthUsed(total);

            // Solo se usa un plan distinto al actual si esta navegación viene de morePlans.
            // Un leftover de localStorage no debe forzar un tier más alto.
            // Si el backend exige otra tarifa (la actual no cabe, es promoción...) se renueva con la requerida.
            var renewalTarget = resolveRenewalTarget(renewalOptions.current, resolveChosenSubscriptionId(), contracts.current.subscriptionId);
            var subscriptionId = renewalTarget.subscriptionId;
            // Una promoción pasa a experience sin recargo y con validez desde hoy, como cobra el backend.
            var currentType = currentSubscriptionType.current ?? contracts.current.subscriptionType;
            var isPromotionRenewal = isPromotionSubscriptionType(currentType);

            if(renewalTarget.mustShowRequiredMessage)
            {
                setRenewalNotice(renewalOptions.current?.message || 'Tu suscripción actual no puede renovarse y se ha seleccionado la suscripción requerida.');
            }

            if(subscriptionId !== undefined)
            {
                //Viene del MorePlans
                //Hay que recalcular el recargo.
                
                // Validar que el subscriptionId es un número válido
                if(Number.isNaN(subscriptionId))
                {
                    console.error('useEffect: subscriptionId no es un número válido');
                    setLoadingPage(false);
                    setInfoMessage('Error al procesar la suscripción. Por favor, contacte con soporte.');
                    return;
                }

                SubscriptionService.getSubscription(subscriptionId).then((data: SubscriptionType) =>
                {
                    // Validar que se recibió data válida
                    if(!data || !data.name || typeof data.price !== 'number' || !data.id)
                    {
                        console.error('useEffect: datos de suscripción inválidos', data);
                        setLoadingPage(false);
                        setInfoMessage('Error al obtener los datos de la suscripción. Por favor, contacte con soporte.');
                        return;
                    }

                    setSubscription(data);
                    setInitialPrice(data.price);

                    var dateConverted = orderDate(contracts.current.dateOfOutdate);
                    
                    if(!dateConverted)
                    {
                        console.error('Error al convertir la fecha de caducidad');
                        setLoadingPage(false);
                        setInfoMessage('Error al procesar la fecha de la suscripción. Por favor, contacte con soporte.');
                        return;
                    }
                    
                    var baseDate = new Date(dateConverted);

                    if(isPromotionRenewal)
                    {
                        baseDate = new Date();
                    }
                    else if(baseDate < today)
                    {
                        baseDate = today;
                    }

                    if(data.name.includes("Mes"))
                    {
                        var newDate = new Date(new Date(baseDate).setMonth(baseDate.getMonth() + 1));
                        setNewDateSubscription(`${String(newDate.getDate()).padStart(2, '0')}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${newDate.getFullYear()}`);
                    }
                    else
                    {
                        var dateFromAnnual = new Date(new Date(baseDate).setFullYear(baseDate.getFullYear() + 1));
                        setNewDateSubscription(`${String(dateFromAnnual.getDate()).padStart(2, '0')}-${String(dateFromAnnual.getMonth() + 1).padStart(2, '0')}-${dateFromAnnual.getFullYear()}`);
                    }

                    var newRecharge = resolveRenewalSurcharge(currentType, recalculateRecharge(new Date(dateConverted), data.price));
                    setRecharge(newRecharge);

                    ElementService.getLastElementsPrice().then((waterPrice: any) =>
                    {
                        // Validar que waterPrice es válido
                        if(typeof waterPrice !== 'number' || isNaN(waterPrice))
                        {
                            console.error('useEffect: waterPrice no es válido', waterPrice);
                            setLoadingPage(false);
                            setInfoMessage('Error al obtener el precio de los Elements. Por favor, contacte con soporte.');
                            return;
                        }

                        setElementWaterPrice(waterPrice);
                        setReference(`${user.id}-S${data.id}-A${activePlan.id}-${today.valueOf().toString()}`);
                        arePricesLoaded.current = true;
                        Auth.deleteRepaymentSubscription();
                        setLoadingPage(false);
                    })
                    .catch((error: any) =>
                    {
                        console.error('useEffect: Error en ElementService.getLastElementsPrice()', error);
                        setLoadingPage(false);
                        setInfoMessage('Error al obtener el precio de los Elements. Por favor, inténtelo de nuevo más tarde.');
                    });
                })
                .catch((error: any) =>
                {
                    console.error('useEffect: Error en SubscriptionService.getSubscription()', error);
                    setLoadingPage(false);
                    setInfoMessage('Error al obtener los datos de la suscripción. Por favor, inténtelo de nuevo más tarde.');
                });
            }
            else
            {
                //No hace falta recalcular el recargo.
                var subscription: SubscriptionType =
                {
                    description: "",
                    subscriptionType: "",
                    period: "",
                    priceProvider: 0,
                    limit: 0,
                    riskLevel: 0,
                    name: contracts.current.subscriptionName,
                    priceIronia: contracts.current.subscriptionPrice,
                    id: contracts.current.subscriptionId,
                    price: contracts.current.subscriptionPrice,
                    type: contracts.current.subscriptionType,
                };

                setSubscription(subscription);
                setInitialPrice(subscription.price as number);

                // Una promoción no lleva recargo (el backend no lo cobra).
                var currentRecharge = resolveRenewalSurcharge(currentType, contracts.current.outdateRecharge);
                if(currentRecharge !== recharge)
                {
                    setRecharge(currentRecharge);
                }

                var convertedDate = orderDate(contracts.current.dateOfOutdate);
                
                if(!convertedDate)
                {
                    console.error('Error al convertir la fecha de caducidad');
                    setLoadingPage(false);
                    setInfoMessage('Error al procesar la fecha de la suscripción. Por favor, contacte con soporte.');
                    return;
                }

                let validDate = new Date(convertedDate);

                if(recharge > 0 || isPromotionRenewal)
                {
                    validDate = new Date(Date.now());
                }

                if(contracts.current.subscriptionPeriod === "month")
                {
                    validDate.setMonth(validDate.getMonth() + 1);
                }
                else
                {
                    validDate.setFullYear(validDate.getFullYear() + 1);
                }
                setNewDateSubscription(`${String(validDate.getDate()).padStart(2, '0')}-${String(validDate.getMonth() + 1).padStart(2, '0')}-${validDate.getFullYear()}`);

                ElementService.getLastElementsPrice().then((waterPrice: any) =>
                {
                    // Validar que waterPrice es válido
                    if(typeof waterPrice !== 'number' || isNaN(waterPrice))
                    {
                        console.error('useEffect: waterPrice no es válido', waterPrice);
                        setLoadingPage(false);
                        setInfoMessage('Error al obtener el precio de los Elements. Por favor, contacte con soporte.');
                        return;
                    }

                    setElementWaterPrice(waterPrice);
                    arePricesLoaded.current = true;
                    Auth.deleteRepaymentSubscription();
                    setLoadingPage(false);
                })
                .catch((error: any) =>
                {
                    console.error('useEffect: Error en ElementService.getLastElementsPrice()', error);
                    setLoadingPage(false);
                    setInfoMessage('Error al obtener el precio de los Elements. Por favor, inténtelo de nuevo más tarde.');
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasValidData, renewalOptionsLoaded]);
    const orderDate = (dateToOrder: string): string | null =>
    {
        // Validar que la fecha existe y no es vacía
        if(!dateToOrder || typeof dateToOrder !== 'string' || dateToOrder.trim() === '')
        {
            console.error('orderDate: fecha inválida o vacía', dateToOrder);
            return null;
        }

        // Validar que la fecha tiene el formato esperado (DD-MM-YYYY)
        const parts = dateToOrder.split("-");
        
        if(parts.length !== 3)
        {
            console.error('orderDate: formato de fecha incorrecto, se esperaba DD-MM-YYYY', dateToOrder);
            return null;
        }

        const [day, month, year] = parts;

        // Validar que todas las partes existen y no están vacías
        if(!day || !month || !year)
        {
            console.error('orderDate: partes de fecha incompletas', { day, month, year });
            return null;
        }

        return `${month}-${day}-${year}`;
    }
    const recalculateRecharge = (dateOutdated: Date, subscriptionPrice: number) =>
    {
        let newRecharge: number = 0;
        
        // Validar que la fecha y el precio son válidos
        if(!dateOutdated || isNaN(dateOutdated.getTime()) || isNaN(subscriptionPrice))
        {
            return newRecharge;
        }

        const subtract = new Date().getTime() - dateOutdated.getTime();
        const totalDays = subtract / (1000 * 60 * 60 * 24);

        if(totalDays < 30)
        {
            return newRecharge;
        }

        if(subscriptionPrice > 0)
        {
            newRecharge = parseFloat(((subscriptionPrice / 12) * Math.round(totalDays / 30) * 100 / 100).toFixed(2));

            if(isNaN(newRecharge))
            {
                newRecharge = 0;
            }
        }

        return newRecharge;
    }
    // Elements puestos y precio de cada tipo, para las reglas que combinan Agua, IA y Tierra de todos los contratos.
    const currentElementInputs = (): TRenewalElementInputs =>
    {
        return { water: inputWaterValue, ia: inputIaValue, earth: inputEarthValue };
    }
    const currentElementPrices = (): TRenewalElementPrices =>
    {
        return { water: elementWaterPrice, ia: 0.01, earth: elementEarthPrice };
    }
    const fullWaterElements = (elementAmountId?: number) =>
    {
        setInfoMessage(undefined);

        // Validar que elementWaterPrice no es cero
        if(elementWaterPrice === 0 || isNaN(elementWaterPrice))
        {
            console.error('fullWaterElements: elementWaterPrice es 0 o NaN');
            setInfoMessage('Error al calcular el precio de los Elements. Por favor, contacte con soporte.');
            return;
        }

        var contractInfo = contracts.current.subscriptionInfo.find(item => item.elementAmountId === elementAmountId);

        // Validar que contractInfo existe
        if(!contractInfo)
        {
            console.error('fullWaterElements: No se encontró información del contrato', elementAmountId);
            setInfoMessage('Error al procesar los Elements. Por favor, contacte con soporte.');
            return;
        }

        // Validar que no hay elementos negativos
        if(contractInfo.elementsWater !== undefined && contractInfo.elementsWater < 0)
        {
            console.error('fullWaterElements: elementsWater tiene valor negativo', contractInfo.elementsWater);
            setInfoMessage('Este contrato tiene un saldo negativo de Elements Agua y no puede ser utilizado como método de pago.');
            return;
        }

        if(finalPrice === 0)
        {
            setInfoMessage("¡El importe de la renovación ya está totalmente cubierto con Elements!");
        }
        else
        {
            if(contractInfo.elementsWater === 0 || contractInfo.elementsWater === undefined)
            {
                setInfoMessage("¡No dispones de Elements Agua para utilizar en este contrato!");
            }
            else
            {
                var index: number = contracts.current.subscriptionInfo.findIndex(item => item.elementAmountId === elementAmountId);

                if(index === -1)
                {
                    console.error('fullWaterElements: No se encontró el contrato con elementAmountId', elementAmountId);
                    setInfoMessage('Error al procesar los Elements. Por favor, contacte con soporte.');
                    return;
                }

                ///Se rellena lo que falta por cubrir contando los Elements de todos los contratos y tipos. Los que ya tenga este input se sustituyen.
                upgradeElements(elementsToFill(initialPrice + recharge, currentElementInputs(), currentElementPrices(), "water", index, contractInfo.elementsWater), "water", elementAmountId);
            }
        }
    }
    const fullIAElements = (elementAmountId?: number) =>
    {
        setInfoMessage(undefined);
        var contractInfo = contracts.current.subscriptionInfo.find(item => item.elementAmountId === elementAmountId);

        // Validar que contractInfo existe
        if(!contractInfo)
        {
            console.error('fullIAElements: No se encontró información del contrato', elementAmountId);
            setInfoMessage('Error al procesar los Elements. Por favor, contacte con soporte.');
            return;
        }

        // Validar que no hay elementos negativos
        if(contractInfo.elementsIA !== undefined && contractInfo.elementsIA < 0)
        {
            console.error('fullIAElements: elementsIA tiene valor negativo', contractInfo.elementsIA);
            setInfoMessage('Este contrato tiene un saldo negativo de Elements IA y no puede ser utilizado como método de pago.');
            return;
        }

        if(finalPrice === 0)
        {
            setInfoMessage("¡El importe de la renovación ya está totalmente cubierto con Elements!");
        }
        else
        {
            if(contractInfo.elementsIA === 0 || contractInfo.elementsIA === undefined)
            {
                setInfoMessage("¡No dispones de Elements IA para utilizar en este contrato!");
            }
            else
            {
                var index: number = contracts.current.subscriptionInfo.findIndex(item => item.elementAmountId === elementAmountId);

                if(index === -1)
                {
                    console.error('fullIAElements: No se encontró el contrato con elementAmountId', elementAmountId);
                    setInfoMessage('Error al procesar los Elements. Por favor, contacte con soporte.');
                    return;
                }

                ///Se rellena lo que falta por cubrir contando los Elements de todos los contratos y tipos. Los que ya tenga este input se sustituyen.
                upgradeElements(elementsToFill(initialPrice + recharge, currentElementInputs(), currentElementPrices(), "ia", index, contractInfo.elementsIA), "ia", elementAmountId);
            }
        }
    }
    const isEarthDisabled = (contractInfo: Contracts): boolean =>
    {
        return elementEarthPrice <= 0 || contractInfo.elementsEarth === undefined || contractInfo.elementsEarth === null || contractInfo.elementsEarth <= 0;
    }
    const fullEarthElements = (elementAmountId?: number) =>
    {
        setInfoMessage(undefined);

        if(elementEarthPrice <= 0)
        {
            setInfoMessage('Los Elements Tierra no están disponibles en este momento. Por favor, inténtelo de nuevo más tarde.');
            return;
        }

        var index: number = contracts.current.subscriptionInfo.findIndex(item => item.elementAmountId === elementAmountId);

        if(index === -1)
        {
            console.error('fullEarthElements: No se encontró el contrato con elementAmountId', elementAmountId);
            setInfoMessage('Error al procesar los Elements. Por favor, contacte con soporte.');
            return;
        }

        const availableEarthElements = contracts.current.subscriptionInfo[index].elementsEarth ?? 0;

        if(availableEarthElements < 0)
        {
            setInfoMessage('Este contrato tiene un saldo negativo de Elements Tierra y no puede ser utilizado como método de pago.');
            return;
        }

        if(finalPrice === 0)
        {
            setInfoMessage("¡El importe de la renovación ya está totalmente cubierto con Elements!");
            return;
        }

        if(availableEarthElements === 0)
        {
            setInfoMessage("¡No dispones de Elements Tierra para utilizar en este contrato!");
            return;
        }

        // Se rellena lo que falta por cubrir contando los Elements de todos los contratos y tipos. Los que ya tenga este input se sustituyen.
        upgradeElements(elementsToFill(initialPrice + recharge, currentElementInputs(), currentElementPrices(), "earth", index, availableEarthElements), "earth", elementAmountId);
    }
    const cleanElementsEarth = (elementAmountId?: number) =>
    {
        setInfoMessage(undefined);
        upgradeElements(0, "earth", elementAmountId);
    }
    const handleInputElementsEarth = (value: string, elementAmountId?: number) =>
    {
        setInfoMessage(undefined);

        if(elementEarthPrice <= 0)
        {
            setInfoMessage('Los Elements Tierra no están disponibles en este momento. Por favor, inténtelo de nuevo más tarde.');
            return;
        }

        var index: number = contracts.current.subscriptionInfo.findIndex(item => item.elementAmountId === elementAmountId);

        if(index === -1)
        {
            console.error('handleInputElementsEarth: No se encontró el contrato con elementAmountId', elementAmountId);
            setInfoMessage('Error al procesar los Elements. Por favor, contacte con soporte.');
            return;
        }

        const availableEarthElements = contracts.current.subscriptionInfo[index].elementsEarth ?? 0;

        if(availableEarthElements < 0)
        {
            setInfoMessage('Este contrato tiene un saldo negativo de Elements Tierra y no puede ser utilizado como método de pago.');
            return;
        }

        var price: number = initialPrice+recharge;

        // Solo permitir números positivos
        const numericValue = value.replace(/[^0-9]/g, '');

        if(numericValue === '')
        {
            upgradeElements(0, "earth", elementAmountId);
            setInfoMessage("No se pueden introducir Elements negativos");
            return;
        }

        const parsedValue = parseInt(numericValue);

        // No se aplica una cantidad mayor que el saldo: el backend también la rechaza.
        if(availableEarthElements < parsedValue)
        {
            setInfoMessage(`La cantidad de Elements Tierra que has intentado introducir (${parsedValue}) no puede utilizarse, ya que es mayor a la cantidad de elements de los que dispones (${availableEarthElements}) en este contrato`);
            return;
        }

        // Como máximo lo que pondría la flecha: lo que falta contando los Elements de todos los contratos y tipos, con este input sustituido.
        if(exceedsElementsNeeded(price, currentElementInputs(), currentElementPrices(), "earth", index, parsedValue))
        {
            setInfoMessage("La cantidad de elements que has introducido para utilizar ya excede a la cantidad del precio de la suscripción, por lo que no puedes añadir más Elements Tierra");
        }
        else
        {
            upgradeElements(parsedValue, "earth", elementAmountId);
        }
    }
    const cleanElementsWater = (elementAmountId?: number) =>
    {
        setInfoMessage(undefined);
        upgradeElements(0, "water", elementAmountId);
    }
    const cleanElementsIA = (elementAmountId?: number) =>
    {
        setInfoMessage(undefined);
        upgradeElements(0, "ia", elementAmountId);
    }
    const handleInputElementsWater = (value: string, elementAmountId?: number) =>
    {
        setInfoMessage(undefined);
        
        // Validar que elementWaterPrice no es cero
        if(elementWaterPrice === 0 || isNaN(elementWaterPrice))
        {
            console.error('handleInputElementsWater: elementWaterPrice es 0 o NaN');
            setInfoMessage('Error al calcular el precio de los Elements. Por favor, contacte con soporte.');
            return;
        }

        var index: number = contracts.current.subscriptionInfo.findIndex(item => item.elementAmountId === elementAmountId);
        
        if(index === -1)
        {
            console.error('handleInputElementsWater: No se encontró el contrato con elementAmountId', elementAmountId);
            setInfoMessage('Error al procesar los Elements. Por favor, contacte con soporte.');
            return;
        }

        // Validar que no hay elementos negativos
        if(contracts.current.subscriptionInfo[index].elementsWater !== undefined && contracts.current.subscriptionInfo[index].elementsWater !== null && contracts.current.subscriptionInfo[index].elementsWater! < 0)
        {
            console.error('handleInputElementsWater: elementsWater tiene valor negativo', contracts.current.subscriptionInfo[index].elementsWater);
            setInfoMessage('Este contrato tiene un saldo negativo de Elements Agua y no puede ser utilizado como método de pago.');
            return;
        }
        
        var price: number = initialPrice+recharge;

        // Solo permitir números positivos
        const numericValue = value.replace(/[^0-9]/g, '');
        
        if(numericValue === '')
        {
            upgradeElements(0, "water", elementAmountId);
            setInfoMessage("No se pueden introducir Elements negativos");
            return;
        }

        const parsedValue = parseInt(numericValue);

        // Validar antes de comparar - obtener el valor seguro
        const availableWaterElements = contracts.current.subscriptionInfo[index].elementsWater ?? 0;

        // No se aplica una cantidad mayor que el saldo: el backend también la rechaza.
        if(availableWaterElements < parsedValue)
        {
            setInfoMessage(`La cantidad de Elements Agua que has intentado introducir (${parsedValue}) no puede utilizarse, ya que es mayor a la cantidad de elements de los que dispones (${availableWaterElements}) en este contrato`);
            return;
        }

        //Como máximo lo que pondría la flecha: lo que falta contando los Elements de todos los contratos y tipos, con este input sustituido.
        if(exceedsElementsNeeded(price, currentElementInputs(), currentElementPrices(), "water", index, parsedValue))
        {
            setInfoMessage("La cantidad de elements que has introducido para utilizar ya excede a la cantidad del precio de la suscripción, por lo que no puedes añadir más Elements Agua");
        }
        else
        {
            upgradeElements(parsedValue, "water", elementAmountId);
        }
    }
    const handleInputElementsIA = (value: string, elementAmountId?: number) =>
    {
        setInfoMessage(undefined);
        var index: number = contracts.current.subscriptionInfo.findIndex(item => item.elementAmountId === elementAmountId);
        
        if(index === -1)
        {
            console.error('handleInputElementsIA: No se encontró el contrato con elementAmountId', elementAmountId);
            setInfoMessage('Error al procesar los Elements. Por favor, contacte con soporte.');
            return;
        }

        // Validar que no hay elementos negativos
        if(contracts.current.subscriptionInfo[index].elementsIA !== undefined && contracts.current.subscriptionInfo[index].elementsIA !== null && contracts.current.subscriptionInfo[index].elementsIA! < 0)
        {
            console.error('handleInputElementsIA: elementsIA tiene valor negativo', contracts.current.subscriptionInfo[index].elementsIA);
            setInfoMessage('Este contrato tiene un saldo negativo de Elements IA y no puede ser utilizado como método de pago.');
            return;
        }
        
        var price: number = initialPrice+recharge;

        // Solo permitir números positivos
        const numericValue = value.replace(/[^0-9]/g, '');
        
        if(numericValue === '')
        {
            upgradeElements(0, "ia", elementAmountId);
            setInfoMessage("No se pueden introducir Elements negativos");
            return;
        }

        const parsedValue = parseInt(numericValue);

        // Validar antes de comparar - obtener el valor seguro
        const availableIAElements = contracts.current.subscriptionInfo[index].elementsIA ?? 0;

        // No se aplica una cantidad mayor que el saldo: el backend también la rechaza.
        if(availableIAElements < parsedValue)
        {
            setInfoMessage(`La cantidad de Elements IA que has intentado introducir (${parsedValue}) no puede utilizarse, ya que es mayor a la cantidad de elements de los que dispones (${availableIAElements}) en este contrato`);
            return;
        }

        //Como máximo lo que pondría la flecha: lo que falta contando los Elements de todos los contratos y tipos, con este input sustituido.
        if(exceedsElementsNeeded(price, currentElementInputs(), currentElementPrices(), "ia", index, parsedValue))
        {
            setInfoMessage("La cantidad de elements que has introducido para utilizar ya excede a la cantidad del precio de la suscripción, por lo que no puedes añadir más Elements IA");
        }
        else
        {
            upgradeElements(parsedValue, "ia", elementAmountId);
        }
    };
    const upgradeElements = (elementsNeccesary: number, type: string, elementAmountId?: number) =>
    {
        const indexInArray = contracts.current.subscriptionInfo.findIndex(item => item.elementAmountId === elementAmountId);

        if(indexInArray === -1)
        {
            console.error('upgradeElements: No se encontró el contrato con elementAmountId', elementAmountId);
            setInfoMessage('Error al procesar los Elements. Por favor, contacte con soporte.');
            return;
        }

        if(elementAmountId !== undefined)
        {
            const newValues: number[] = type === "water" ? [...inputWaterValue] : type === "earth" ? [...inputEarthValue] : [...inputIaValue];
            newValues[indexInArray] = Math.ceil(elementsNeccesary);
            var total = newValues.reduce((accumulator, currentValue) => accumulator + currentValue, 0);

            if(type === "water")
            {
                setInputWaterValue(newValues);
                setElementsWaterUsed(total);
            }
            else if(type === "ia")
            {
                setInputIaValue(newValues);
                setElementsIAUsed(total);
            }
            else if(type === "earth")
            {
                setInputEarthValue(newValues);
                setElementsEarthUsed(total);
            }
        }
    }
    React.useEffect(() =>
    {
        var finalPrice: number = initialPrice+recharge-(elementsIAUsed * 0.01)-(elementsWaterUsed * elementWaterPrice)-(elementsEarthUsed * elementEarthPrice);

        if(finalPrice < 0)
        {
            finalPrice = 0.00;
        }

        var finalPriceStr: string = finalPrice.toFixed(2);
        setFinalPrice(parseFloat(finalPriceStr));
    }, [elementsIAUsed, elementsWaterUsed, elementWaterPrice, elementsEarthUsed, elementEarthPrice, initialPrice, recharge]);
    const sendPayment = async (event: any) =>
    {
        event.preventDefault();
        
        // Validar que el usuario está autenticado
        if(!user || !user.id)
        {
            console.error('sendPayment: Usuario no autenticado');
            setInfoMessage('Error de autenticación. Por favor, inicia sesión nuevamente.');
            return;
        }

        // El backend no permite renovar esta suscripción
        if(renewalBlockedReason !== undefined)
        {
            setInfoMessage(renewalBlockedReason);
            return;
        }

        // Validar que hay una suscripción seleccionada
        if(!newSubscription || !newSubscription.id)
        {
            console.error('sendPayment: No hay suscripción seleccionada');
            setInfoMessage('Error al procesar la suscripción. Por favor, contacte con soporte.');
            return;
        }

        setIsSubmitted(true);

        var priceToPay: number = parseFloat(finalPrice.toFixed(2));
        const subject = buildSubscriptionSubject(user.id, newSubscription.id);

        PaymentService.createPayment(
        {
            amount: priceToPay,
            reference: reference,
            subject: subject,
            status: 1,
            usersSubscriptionId: contracts.current.userSubscriptionId,
            platform: "INESPAY"
        })
        .then(() =>
        {
            var inespayData: InespayType =
            {
                subject: subject,
                reference: reference,
                amount: (priceToPay * 100).toString(),
                subscription: true,
                renovation: true,
                oldSubscriptionId: contracts.current.subscriptionId,
                subscriptionId: newSubscription.id,
                usersSubscriptionId: contracts.current.userSubscriptionId,
                userId: user.id
            };

            if(subscriptionHasNoElements === false)
            {      
                var contractsElements: any = [];
                
                contracts.current.subscriptionInfo.map((item, index) =>
                {
                    return(
                        contractsElements.push(
                        {
                            elementAmountId: item.elementAmountId,
                            water: -1 * inputWaterValue[index],
                            ia: -1 * inputIaValue[index],
                            earth: -1 * (inputEarthValue[index] ?? 0)
                        })
                    );
                });
                    
                inespayData.contractsElements = contractsElements;
            }
            // Validar que existe al menos un elemento antes de acceder al índice 0
            if(contracts.current.subscriptionInfo.length > 0 && contracts.current.subscriptionInfo[0].isSubscriptionRenewalBeforeExpiration !== undefined)
            {
                //Como todas van con el mismo, cogemos la primera.
                inespayData.isPlanRenewalBeforeExpiration = contracts.current.subscriptionInfo[0].isSubscriptionRenewalBeforeExpiration;
            }

            sendToInespay(inespayData);
        },
        (error: any) =>
        {
            console.error('sendPayment: Error en PaymentService.createPayment()', error);
            const errorMessage = typeof error === 'string' ? error : 'Error al crear el pago. Por favor, inténtelo de nuevo más tarde.';
            setInfoMessage(errorMessage);
            setIsSubmitted(false);
        });
    };
    const sendToInespay = (inespayData: InespayType) =>
    {
        if(subscriptionHasNoElements === false && finalPrice === 0)
        {
            ///Tenemos Elements que cubren el importe y no vamos a Inespay
            InespayService.postNoPriceInespay(inespayData).then((dataInespay: InespayNoPrice) =>
            {
                if(dataInespay.isError === true)
                {
                    setInfoMessage(dataInespay.statusMessage);
                    setIsSubmitted(false);
                }
                else
                {
                    if(dataInespay.statusMessage === "Inespay")
                    {
                        InespayService.postInespay(inespayData).then((dataInespay: any) =>
                        {
                            const inespay_result = parseInespayResult(dataInespay);

                            if(!inespay_result)
                            {
                                console.error('postInespay: respuesta inválida', dataInespay);
                                setInfoMessage("Error al procesar la respuesta del servidor. Por favor, inténtelo de nuevo más tarde.");
                                setIsSubmitted(false);
                                return;
                            }

                            if(isInespaySuccess(inespay_result))
                            {
                                window.location.href = inespay_result.url;
                            }
                            else
                            {
                                setInfoMessage(inespay_result.description);
                                setIsSubmitted(false);
                            }
                        },
                        (error: any) =>
                        {
                            console.error('postInespay: error en la llamada', error);
                            setInfoMessage("Se ha producido un error al procesar su transacción. Por favor, inténtelo de nuevo más tarde.");
                            setIsSubmitted(false);
                        });
                    }
                    else
                    {
                        window.location.href = dataInespay.statusMessage;
                    }
                }
            })
            .catch((error: any) =>
            {
                console.error('postNoPriceInespay: error en la llamada', error);
                setInfoMessage("Se ha producido un error al procesar su transacción. Por favor, inténtelo de nuevo más tarde.");
                setIsSubmitted(false);
            });
        }
        else
        {
            //No cubrimos el importe íntegro y vamos a Inespay
            if(parseFloat(inespayData.amount) > 0 && parseFloat(inespayData.amount) < 100)
            {
                setInfoMessage("El valor de compra con Elements debe ser completamente 0€ o mayor de 1€ para poder procesar la transacción. Los valores entre 0.01€ y 1€ no son aceptados por la plataforma PSD2.");
                setIsSubmitted(false);
            }
            else
            {
                setIsSubmitted(true);
                InespayService.postInespay(inespayData).then((dataInespay: any) =>
                {
                    const inespay_result = parseInespayResult(dataInespay);

                    if(!inespay_result)
                    {
                        console.error('postInespay: respuesta inválida', dataInespay);
                        setInfoMessage("Error al procesar la respuesta del servidor. Por favor, inténtelo de nuevo más tarde.");
                        setIsSubmitted(false);
                        return;
                    }

                    if(isInespaySuccess(inespay_result))
                    {
                        window.location.href = inespay_result.url;
                    }
                    else
                    {
                        setInfoMessage(inespay_result.description);
                        setIsSubmitted(false);
                    }
                },
                (error: any) =>
                {
                    setInfoMessage("Se ha producido un error al procesar su transacción. Por favor, inténtelo de nuevo más tarde.");
                    setIsSubmitted(false);
                });
            }
        }
    };

    return(
        <DocumentTitle title='IronIA - Renovación de Suscripción'>
            <React.Fragment>
                <Style.DivRowPrincipal className="ms-Grid-row">
                    <Style.PrincipalHeader>
                        Renovación de la Suscripción
                    </Style.PrincipalHeader>
                    <Style.PrincipalDescription>
                        Este método de pago conecta con tu banca online utilizando las claves de acceso de tu banca online.
                    </Style.PrincipalDescription>
                    <Separator className="separatorRepayment" style={{marginTop: "0 !important"}} />
                </Style.DivRowPrincipal>
                {(!hasValidData || !user) &&
                (
                    <Style.Card style={{marginTop: "2em"}}>
                        <Style.CardTitle style={{color: "#CC214F"}}>
                            {!user ? 'Error de autenticación' : 'Error al cargar la información'}
                        </Style.CardTitle>
                        <div style={{padding: "2em", textAlign: "center"}}>
                            {!user ?
                            (
                                <React.Fragment>
                                    <p style={{marginBottom: "1em"}}>
                                        No se ha podido verificar tu sesión de usuario.
                                    </p>
                                    <p style={{fontWeight: "bold"}}>
                                        Por favor, inicia sesión nuevamente para continuar.
                                    </p>
                                </React.Fragment>
                            ) :
                            (
                                <React.Fragment >
                                    <p style={{marginBottom: "1em"}}>
                                        No se han recibido los datos necesarios para procesar la renovación de tu suscripción.
                                    </p>
                                    <p style={{marginBottom: "1em"}}>
                                        Esto puede ocurrir si has accedido a esta página directamente mediante URL o si has recargado la página.
                                    </p>
                                    <p style={{fontWeight: "bold"}}>
                                        Por favor, vuelve a la página de Mis Planes y selecciona nuevamente la opción de renovación.
                                    </p>
                                </React.Fragment>
                            )}
                        </div>
                    </Style.Card>
                )}
                {hasValidData && user && loadingPage === true &&
                (
                    <React.Fragment>
                        <Spinner size={SpinnerSize.large} style={{paddingTop: "2em"}} />
                        <Style.TextLoading>
                            Estamos cargando todos los datos necesarios asociados para la Renovación de tu Suscripción...
                        </Style.TextLoading>
                    </React.Fragment>
                )}
                {hasValidData && user && loadingPage === false &&
                (
                    <React.Fragment>
                        {subscriptionHasNoElements === false &&
                        (
                            <Style.Card>
                                <Style.CardTitle>
                                    Utiliza tus Elements
                                </Style.CardTitle>
                                <Style.TableContainer>
                                    <Style.TableHeaderRow>
                                        <Style.TableHeader>Plan de Cartera</Style.TableHeader>
                                        <Style.TableHeader>Contrato</Style.TableHeader>
                                        <Style.TableHeader>
                                            Elements &nbsp; Tierra
                                            <br />
                                            <Style.SpanIntraCell>
                                                Precio
                                            </Style.SpanIntraCell>
                                            <span>
                                                Valor
                                            </span>
                                        </Style.TableHeader>
                                        <Style.TableHeader>
                                            Elements &nbsp; Agua
                                            <br />
                                            <Style.SpanIntraCell>
                                                Precio
                                            </Style.SpanIntraCell>
                                            <span>
                                                Valor
                                            </span>
                                        </Style.TableHeader>
                                        <Style.TableHeader>Elements IA</Style.TableHeader>
                                        <Style.TableHeader>Elements Tierra</Style.TableHeader>
                                        <Style.TableHeader>Elements Agua</Style.TableHeader>
                                        <Style.TableHeader>Elements IA</Style.TableHeader>
                                    </Style.TableHeaderRow>
                                    {contracts?.current.subscriptionInfo.map((contractInfo: Contracts, index: number) =>
                                    (
                                        <Style.ContractRow key={index}>
                                            <Style.TableCell className="plan">{contractInfo.savingPlanName}</Style.TableCell>
                                            <Style.TableCell className="contract">
                                                <Style.CellCaption>Contrato</Style.CellCaption>
                                                {contractInfo.contract}
                                            </Style.TableCell>
                                            <Style.TableCell className="earthInfo">
                                                <Style.CellLabel>Elements Tierra</Style.CellLabel>
                                                <Style.SpanIntraCell>
                                                    <Style.CellCaption>Precio</Style.CellCaption>
                                                    {longDecimalFormatter.format(elementEarthPrice)} €
                                                </Style.SpanIntraCell>
                                                <Style.ValueWithAction>
                                                    <Style.CellCaption>Valor</Style.CellCaption>
                                                    {contractInfo.elementsEarth !== undefined && contractInfo.elementsEarth !== null ? decimalFormatter.format(contractInfo.elementsEarth) : 0}
                                                    {contractInfo.hasElements === true &&
                                                    (
                                                        <Style.SpanButton onClick={() => fullEarthElements(contractInfo.elementAmountId)}>
                                                            <CustomIcon disabled={isEarthDisabled(contractInfo) || finalPrice === 0} size={"15"} color={"#CC214F"} iconName={"NavigateForward"} marginLeft={"0.5em"} />
                                                        </Style.SpanButton>
                                                    )}
                                                    {contractInfo.hasElements === false &&
                                                    (
                                                        <Style.SpanButton style={{cursor: "not-allowed"}}>
                                                            <CustomIcon size={"15"} disabled={true} iconName={"NavigateForward"} marginLeft={"0.5em"} />
                                                        </Style.SpanButton>
                                                    )}
                                                </Style.ValueWithAction>
                                            </Style.TableCell>
                                            <Style.TableCell className="waterInfo">
                                                <Style.CellLabel>Elements Agua</Style.CellLabel>
                                                <Style.SpanIntraCell>
                                                    <Style.CellCaption>Precio</Style.CellCaption>
                                                    {longDecimalFormatter.format(elementWaterPrice)} €
                                                </Style.SpanIntraCell>
                                                <Style.ValueWithAction>
                                                    <Style.CellCaption>Valor</Style.CellCaption>
                                                    {contractInfo.elementsWater !== undefined ? decimalFormatter.format(contractInfo.elementsWater) : 0}
                                                    {contractInfo.hasElements === true &&
                                                    (
                                                        <Style.SpanButton onClick={() => fullWaterElements(contractInfo.elementAmountId)}>
                                                            <CustomIcon disabled={contractInfo.elementsWater === 0 || contractInfo.elementsWater === undefined || (contractInfo.elementsWater !== undefined && contractInfo.elementsWater < 0) || finalPrice === 0} size={"15"} color={"#CC214F"} iconName={"NavigateForward"} marginLeft={"0.5em"} />
                                                        </Style.SpanButton>
                                                    )}
                                                    {contractInfo.hasElements === false &&
                                                    (
                                                        <Style.SpanButton style={{cursor: "not-allowed"}}>
                                                            <CustomIcon size={"15"} disabled={true} iconName={"NavigateForward"} marginLeft={"0.5em"} />
                                                        </Style.SpanButton>
                                                    )}
                                                </Style.ValueWithAction>
                                            </Style.TableCell>
                                            <Style.TableCell className="iaInfo">
                                                <Style.CellLabel>Elements IA</Style.CellLabel>
                                                <Style.ValueWithAction>
                                                    <Style.CellCaption>Valor</Style.CellCaption>
                                                    {longDecimalFormatter.format(contractInfo.elementsIA || 0)}
                                                    {contractInfo.hasElements === true &&
                                                    (
                                                        <Style.SpanButton onClick={() => fullIAElements(contractInfo.elementAmountId)}>
                                                            <CustomIcon disabled={contractInfo.elementsIA === 0 || contractInfo.elementsIA === undefined || (contractInfo.elementsIA !== undefined && contractInfo.elementsIA < 0) || finalPrice === 0} size={"15"} color={"#CC214F"} iconName={"NavigateForward"} marginLeft={"0.5em"} />
                                                        </Style.SpanButton>
                                                    )}
                                                    {contractInfo.hasElements === false &&
                                                    (
                                                        <Style.SpanButton style={{cursor: "not-allowed"}}>
                                                            <CustomIcon size={"15"} disabled={true} iconName={"NavigateForward"} marginLeft={"0.5em"} />
                                                        </Style.SpanButton>
                                                    )}
                                                </Style.ValueWithAction>
                                            </Style.TableCell>
                                            <Style.ElementsGroup className="earthInput">
                                                <Style.ElementsInput disabled={isEarthDisabled(contractInfo)} type="text" value={inputEarthValue[index]} onChange={(e) => handleInputElementsEarth(e.target.value, contractInfo.elementAmountId)} />
                                                {contractInfo.hasElements === true &&
                                                (
                                                    <Style.ElementsButton onClick={() => cleanElementsEarth(contractInfo.elementAmountId)}>
                                                        <CustomIcon disabled={isEarthDisabled(contractInfo) || inputEarthValue[index] === 0} iconName='EraseTool' size={"15"} color={"#CC214F"} />
                                                    </Style.ElementsButton>
                                                )}
                                                {contractInfo.hasElements === false &&
                                                (
                                                    <Style.ElementsButton>
                                                        <CustomIcon iconName='EraseTool' disabled={true} size={"15"} color={"#CC214F"} />
                                                    </Style.ElementsButton>
                                                )}
                                            </Style.ElementsGroup>
                                            <Style.ElementsGroup className="waterInput">
                                                <Style.ElementsInput disabled={contractInfo.elementsWater === 0 || contractInfo.elementsWater === undefined || (contractInfo.elementsWater !== undefined && contractInfo.elementsWater < 0)} type="text" value={inputWaterValue[index]} onChange={(e) => handleInputElementsWater(e.target.value, contractInfo.elementAmountId)} />
                                                {contractInfo.hasElements === true &&
                                                (
                                                    <Style.ElementsButton onClick={() => cleanElementsWater(contractInfo.elementAmountId)}>
                                                        <CustomIcon disabled={contractInfo.elementsWater === 0 || contractInfo.elementsWater === undefined || (contractInfo.elementsWater !== undefined && contractInfo.elementsWater < 0) || inputWaterValue[index] === 0} iconName='EraseTool' size={"15"} color={"#CC214F"} />
                                                    </Style.ElementsButton>
                                                )}
                                                {contractInfo.hasElements === false &&
                                                (
                                                    <Style.ElementsButton>
                                                        <CustomIcon iconName='EraseTool' disabled={true} size={"15"} color={"#CC214F"} />
                                                    </Style.ElementsButton>
                                                )}
                                            </Style.ElementsGroup>
                                            <Style.ElementsGroup className="iaInput">
                                                <Style.ElementsInput disabled={contractInfo.elementsIA === 0 || contractInfo.elementsIA === undefined || (contractInfo.elementsIA !== undefined && contractInfo.elementsIA < 0)} type="text" value={inputIaValue[index]} onChange={(e) => handleInputElementsIA(e.target.value, contractInfo.elementAmountId)} />
                                                {contractInfo.hasElements === true &&
                                                (
                                                    <Style.ElementsButton onClick={() => cleanElementsIA(contractInfo.elementAmountId)}>
                                                        <CustomIcon disabled={contractInfo.elementsIA === 0 || contractInfo.elementsIA === undefined || (contractInfo.elementsIA !== undefined && contractInfo.elementsIA < 0) || inputIaValue[index] === 0} iconName='EraseTool' size={"15"} color={"#CC214F"} />
                                                    </Style.ElementsButton>
                                                )}
                                                {contractInfo.hasElements === false &&
                                                (
                                                    <Style.ElementsButton>
                                                        <CustomIcon disabled={true} iconName='EraseTool' size={"15"} color={"#CC214F"} />
                                                    </Style.ElementsButton>
                                                )}
                                            </Style.ElementsGroup>
                                        </Style.ContractRow>
                                    ))}
                                </Style.TableContainer>
                                {infoMessage !== undefined &&
                                (
                                    <Style.FlexContainer>
                                        <Style.TextRightAligned>
                                            <Style.InfoIconStyled id="repayment" />
                                            {infoMessage}
                                        </Style.TextRightAligned>
                                    </Style.FlexContainer>
                                )}
                            </Style.Card>
                        )}
                        <Style.CardContainer>
                            <Style.PaymentCard>
                                <Style.RowSubscriptionCard className="ms-Grid-row">
                                    <Style.ColSubscriptionTitle className="ms-Grid-col ms-sm12 ms-lg7 ms-xl6">
                                        <Style.CardTitle>
                                            Pago de la suscripción
                                        </Style.CardTitle>
                                    </Style.ColSubscriptionTitle>
                                    {(contracts.current.subscriptionType === "experience" ||
                                        contracts.current.subscriptionType === "promotion" ||
                                        contracts.current.subscriptionType === "promotedSubscription") &&
                                    (
                                        <Style.ColSubscriptionButton className="ms-Grid-col ms-sm12 ms-lg5 ms-xl6">
                                            <Style.MoreOptionsWrapper>
                                                <Style.MoreOptions to={
                                                    {
                                                        pathname: "/Repayment/morePlans",
                                                        state: {contracts: contracts.current}
                                                    }}
                                                >
                                                    Más opciones de Renovación
                                                </Style.MoreOptions>
                                            </Style.MoreOptionsWrapper>
                                        </Style.ColSubscriptionButton>
                                    )}
                                </Style.RowSubscriptionCard>
                                {(renewalBlockedReason !== undefined || renewalNotice !== undefined) &&
                                (
                                    <p style={{fontWeight: "bold", color: "#CC214F"}}>
                                        {renewalBlockedReason ?? renewalNotice}
                                    </p>
                                )}
                                <Style.PaymentBreakdown>
                                    <Style.PaymentItem>
                                        <Style.PaymentLabel>Suscripción {newSubscription?.name || 'N/A'}</Style.PaymentLabel>
                                        <Style.PaymentValue>+ {decimalFormatter.format(newSubscription?.price || 0)} €</Style.PaymentValue>
                                    </Style.PaymentItem>
                                    {subscriptionHasNoElements === false &&
                                    (
                                        <React.Fragment>
                                            <Style.PaymentItem>
                                                <Style.PaymentLabel>Elements Tierra</Style.PaymentLabel>
                                                <Style.PaymentValue>- {decimalFormatter.format(parseFloat(earthValueUsed.toFixed(2)))} €</Style.PaymentValue>
                                            </Style.PaymentItem>
                                            <Style.PaymentItem>
                                                <Style.PaymentLabel>Elements Agua</Style.PaymentLabel>
                                                <Style.PaymentValue>- {decimalFormatter.format(parseFloat((elementsWaterUsed * elementWaterPrice).toFixed(2)))} €</Style.PaymentValue>
                                            </Style.PaymentItem>
                                            <Style.PaymentItem>
                                                <Style.PaymentLabel>Elements IA</Style.PaymentLabel>
                                                <Style.PaymentValue>- {decimalFormatter.format(elementsIAUsed * 0.01)} €</Style.PaymentValue>
                                            </Style.PaymentItem>
                                        </React.Fragment>
                                    )}
                                    <Style.PaymentItem>
                                        <Style.PaymentLabel>Retrasos</Style.PaymentLabel>
                                        <Style.PaymentValue>+ {decimalFormatter.format(recharge)} €</Style.PaymentValue>
                                    </Style.PaymentItem>
                                    <Style.PaymentTotal>
                                        <Style.PaymentLabel>Total</Style.PaymentLabel>
                                        <Style.PaymentValue>{decimalFormatter.format(finalPrice)} €</Style.PaymentValue>
                                    </Style.PaymentTotal>
                                </Style.PaymentBreakdown>
                                <Style.PaymentInfo>
                                    <p>Renovación hasta el <Style.RenewalDate>{newDateSubscription}</Style.RenewalDate></p>
                                    <p>Beneficiario</p>
                                    <p>DIAPHANUM VALORES, SV, S.A.U</p>
                                    <p>A87637450 Maria de Molina 40, Planta 7 - Madrid 28006</p>
                                </Style.PaymentInfo>
                                {isSubmitted &&
                                (
                                    <React.Fragment>
                                        <Style.PayButtonDisabled disabled={true}>
                                            <Style.SpinnerContainer>
                                                <Spinner size={SpinnerSize.small} className="button-spinner" />
                                                <Style.SpinnerText>
                                                    Pagando...
                                                </Style.SpinnerText>
                                            </Style.SpinnerContainer>
                                        </Style.PayButtonDisabled>
                                    </React.Fragment>
                                )}
                                {!isSubmitted &&
                                (
                                    <React.Fragment>
                                        <Style.PayButton onClick={sendPayment} disabled={renewalBlockedReason !== undefined} style={renewalBlockedReason !== undefined ? {opacity: 0.5, cursor: "not-allowed"} : undefined}>
                                            Pagar
                                        </Style.PayButton>
                                    </React.Fragment>
                                )}
                            </Style.PaymentCard>
                            <Style.PaymentInfoBox>
                                <p>El retraso en el pago de la suscripción implica el pago del tiempo desde que caducó hasta que la suscripción se renueva por el período especificado.</p>
                                <p>Si se cancela una suscripción se devolverá el dinero correspondiente al tiempo del servicio no disfrutado.</p>
                            </Style.PaymentInfoBox>
                        </Style.CardContainer>
                    </React.Fragment>
                )}
            </React.Fragment>
        </DocumentTitle>
    );
};

export default RePaymentSubscription;